import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { createSelector } from "reselect";
import api from "@/services/api";

export interface AttendanceRecord {
  id: string;
  employeeId: string;
  name: string;
  date: string;
  checkIn: string | null;
  checkOut: string | null;
  status: "present" | "absent" | "late" | "half-day";
  notes: string | null;
}

export interface AttendanceFilter {
  employeeId?: string;
  status?: string;
  date?: string;
  startDate?: string;
  endDate?: string;
}

export interface AttendanceSort {
  field: keyof AttendanceRecord | null;
  direction: "asc" | "desc";
}

interface AttendanceState {
  items: AttendanceRecord[];
  loading: boolean;
  error: string | null;
  filter: AttendanceFilter;
  sort: AttendanceSort;
  currentPage: number;
  pageSize: number;
  total: number;
}

const initialState: AttendanceState = {
  items: [],
  loading: false,
  error: null,
  filter: {},
  sort: { field: null, direction: "asc" },
  currentPage: 1,
  pageSize: 10,
  total: 0,
};

const normalizeAttendance = (payload: any): AttendanceRecord => ({
  id: String(payload.id),
  employeeId: String(payload.employeeId),
  name: payload.name,
  date: payload.date,
  checkIn: payload.checkIn,
  checkOut: payload.checkOut,
  status: payload.status,
  notes: payload.notes,
});

export const fetchAttendance = createAsyncThunk(
  "attendance/fetchAttendance",
  async (params: { date?: string; start_date?: string; end_date?: string } = {}, { rejectWithValue }) => {
    try {
      const response = await api.get("/v1/attendance", { params });
      const raw = Array.isArray(response.data) ? response.data : (response.data?.data ?? []);
      return raw.map(normalizeAttendance);
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || "Failed to fetch attendance");
    }
  }
);

export const createAttendance = createAsyncThunk(
  "attendance/createAttendance",
  async (
    data: {
      employeeId: string;
      date: string;
      checkIn?: string;
      checkOut?: string;
      status: "present" | "absent" | "late" | "half-day";
      notes?: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await api.post("/v1/attendance", {
        employee_id: data.employeeId,
        date: data.date,
        check_in: data.checkIn,
        check_out: data.checkOut,
        status: data.status,
        notes: data.notes,
      });
      return normalizeAttendance(response.data);
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || "Failed to create attendance record");
    }
  }
);

export const updateAttendance = createAsyncThunk(
  "attendance/updateAttendance",
  async (
    { id, data }: {
      id: string;
      data: {
        checkIn?: string;
        checkOut?: string;
        status: "present" | "absent" | "late" | "half-day";
        notes?: string;
      };
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await api.patch(`/v1/attendance/${id}`, {
        check_in: data.checkIn,
        check_out: data.checkOut,
        status: data.status,
        notes: data.notes,
      });
      return normalizeAttendance(response.data);
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || "Failed to update attendance record");
    }
  }
);

export const deleteAttendance = createAsyncThunk(
  "attendance/deleteAttendance",
  async (id: string, { rejectWithValue }) => {
    try {
      await api.delete(`/v1/attendance/${id}`);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || "Failed to delete attendance record");
    }
  }
);

const attendanceSlice = createSlice({
  name: "attendance",
  initialState,
  reducers: {
    setAttendanceFilter: (state, action: PayloadAction<AttendanceFilter>) => {
      state.filter = action.payload;
      state.currentPage = 1;
    },
    setAttendanceSort: (state, action: PayloadAction<AttendanceSort>) => {
      state.sort = action.payload;
      state.currentPage = 1;
    },
    setAttendancePage: (state, action: PayloadAction<number>) => {
      state.currentPage = action.payload;
    },
    setAttendancePageSize: (state, action: PayloadAction<number>) => {
      state.pageSize = action.payload;
      state.currentPage = 1;
    },
    resetAttendanceFilters: (state) => {
      state.filter = {};
      state.sort = { field: null, direction: "asc" };
      state.currentPage = 1;
    },
    addAttendance: (state, action: PayloadAction<AttendanceRecord>) => {
      state.items.unshift(action.payload);
      state.total += 1;
    },
    updateAttendanceItem: (state, action: PayloadAction<AttendanceRecord>) => {
      const index = state.items.findIndex((a) => a.id === action.payload.id);
      if (index !== -1) {
        state.items[index] = action.payload;
      }
    },
    deleteAttendanceItem: (state, action: PayloadAction<string>) => {
      state.items = state.items.filter((a) => a.id !== action.payload);
      state.total -= 1;
    },
    resetAttendance: () => {
      return initialState;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAttendance.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAttendance.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload;
        state.total = action.payload.length;
        state.error = null;
      })
      .addCase(fetchAttendance.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(createAttendance.fulfilled, (state, action) => {
        state.items.unshift(action.payload);
        state.total += 1;
      })
      .addCase(updateAttendance.fulfilled, (state, action) => {
        const index = state.items.findIndex((a) => a.id === action.payload.id);
        if (index !== -1) {
          state.items[index] = action.payload;
        }
      })
      .addCase(deleteAttendance.fulfilled, (state, action) => {
        state.items = state.items.filter((a) => a.id !== action.payload);
        state.total -= 1;
      });
  },
});

export const {
  setAttendanceFilter,
  setAttendanceSort,
  setAttendancePage,
  setAttendancePageSize,
  resetAttendanceFilters,
  addAttendance,
  updateAttendanceItem,
  deleteAttendanceItem,
  resetAttendance,
} = attendanceSlice.actions;
export default attendanceSlice.reducer;

// Selectors
export const selectAttendance = (state: any) => state.attendance.items;
export const selectAttendanceLoading = (state: any) => state.attendance.loading;
export const selectAttendanceError = (state: any) => state.attendance.error;
export const selectAttendanceFilter = (state: any) => state.attendance.filter;
export const selectAttendanceSort = (state: any) => state.attendance.sort;
export const selectAttendancePagination = (state: any) => ({
  currentPage: state.attendance.currentPage,
  pageSize: state.attendance.pageSize,
  total: state.attendance.total,
});

export const selectFilteredAttendance = createSelector(
  [(state: any) => state.attendance.items, (state: any) => state.attendance.filter, (state: any) => state.attendance.sort, (state: any) => state.attendance.currentPage, (state: any) => state.attendance.pageSize],
  (items, filter, sort, currentPage, pageSize) => {
    let filtered = [...items];

    if (filter.employeeId) {
      filtered = filtered.filter((a) => a.employeeId === filter.employeeId);
    }

    if (filter.status) {
      filtered = filtered.filter((a) => a.status === filter.status);
    }

    if (filter.date) {
      filtered = filtered.filter((a) => a.date === filter.date);
    }

    if (filter.startDate && filter.endDate) {
      filtered = filtered.filter((a) => a.date >= filter.startDate! && a.date <= filter.endDate!);
    }

    if (sort.field) {
      filtered.sort((a, b) => {
        const aValue = a[sort.field!];
        const bValue = b[sort.field!];

        if (aValue < bValue) return sort.direction === "asc" ? -1 : 1;
        if (aValue > bValue) return sort.direction === "asc" ? 1 : -1;
        return 0;
      });
    }

    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;

    return {
      items: filtered.slice(startIndex, endIndex),
      total: filtered.length,
    };
  }
);
